import { getSystemDb, wakeLocalWrite } from '@firesquirrel/local-db';
import { apiFetch } from '../apiClient';
import { computeAuditChanges, writeAuditLog } from './systemAuditLogService';
import {
  filterLookupValues,
  normalizeLookupEntityCategories,
  normalizeLookupInspectionModules,
  DEFAULT_LOOKUP_SCOPE_POLICY,
  type LookupScopePolicy,
  type LookupFilterContext,
} from '@firesquirrel/core-domain';

export type LookupScope = 'system' | 'organization' | 'tenant';
export type InheritancePolicy = 'seed_hideable' | 'locked_mandatory' | 'custom_owned';

export interface LookupCategory {
  _id?: string;
  lookupType: string;
  displayName: string;
  description: string;
  module?: string | string[];
  availableScopes: LookupScope[];
  manageableScopes: LookupScope[];
  isSystemManaged: boolean;
  scopePolicy?: LookupScopePolicy;
  scope?: LookupScope;
  originScope?: LookupScope;
  isActive?: boolean;
  _deleted?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface LookupValue {
  _id?: string;
  id: string;
  lookupType: string;
  code: string;
  name: string;
  description?: string;
  createdAt?: string;
  updatedAt?: string;
  scope: LookupScope;
  originScope: LookupScope;
  inheritancePolicy: InheritancePolicy;
  tenantCanHideInherited?: boolean;
  orgCanHideInherited?: boolean;
  sortOrder: number;
  isActive: boolean;
  _deleted?: boolean;
  isSystemDefault?: boolean;
  isHiddenAtLowerLevels?: boolean;
  appliesTo?: string[];
  category?: string | string[];
  inspectionModules?: string[];
  group?: LookupGroupInfo;
  editPolicy?: LookupEditPolicy;
  display?: LookupDisplay;
  config?: Record<string, unknown>;
  requiredModules?: string[];
  aiMetadata?: AiMetadata;
}

export interface ListLookupValuesOptions extends LookupFilterContext {
  activeModules?: string[];
}

export interface AiMetadata {
  aiDescription?: string;
  aiTags?: string[];
  aiSynonyms?: string[];
}

export interface LookupGroupInfo {
  groupId: string;
  groupCode: string;
  groupName: string;
  groupIcon?: string;
  groupColor?: string;
  groupSortOrder: number;
}

export interface LookupGroup {
  _id?: string;
  id: string;
  lookupType: string;
  code: string;
  name: string;
  description?: string;
  scope: LookupScope;
  parentGroupId?: string;
  icon?: string;
  color?: string;
  sortOrder: number;
  isActive: boolean;
  isSystemDefault: boolean;
  editPolicy?: LookupEditPolicy;
  createdAt: string;
  updatedAt: string;
  _deleted?: boolean;
}

export interface LookupDisplay {
  textColor?: string;
  backgroundColor?: string;
  fontWeight?: 'normal' | 'medium' | 'semibold' | 'bold';
  fontStyle?: 'normal' | 'italic';
  badgeStyle?: 'solid' | 'outline' | 'subtle';
  icon?: string;
  iconColor?: string;
  strikethrough?: boolean;
  uppercase?: boolean;
  cssClass?: string;
}

export interface LookupEditPolicy {
  orgCanEdit?: boolean;
  orgCanHide?: boolean;
  tenantCanEdit?: boolean;
  tenantCanHide?: boolean;
  lockedFields?: string[];
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface ListParams {
  page?: number;
  limit?: number;
  search?: string;
}

export interface SyncToTenantsResult {
  message: string;
  tenantCount: number;
  systemLookups: number;
  systemCategories: number;
  systemGroups: number;
}

function nowIso(): string {
  return new Date().toISOString();
}

function normalizeCategoryModules(module: unknown): string[] | undefined {
  if (typeof module === 'string') {
    const trimmed = module.trim();
    return trimmed ? [trimmed] : undefined;
  }
  if (Array.isArray(module)) {
    const normalized = module.map((item) => typeof item === 'string' ? item.trim() : '').filter(Boolean);
    return normalized.length > 0 ? Array.from(new Set(normalized)) : undefined;
  }
  return undefined;
}

function normalizeValueModules(modules: unknown): string[] | undefined {
  if (typeof modules === 'string') {
    const trimmed = modules.trim();
    return trimmed ? [trimmed] : undefined;
  }
  if (Array.isArray(modules)) {
    const normalized = modules
      .map((item) => typeof item === 'string' ? item.trim() : '')
      .filter(Boolean);
    return normalized.length > 0 ? Array.from(new Set(normalized)) : undefined;
  }
  return undefined;
}

function isMongoId(value: string | undefined | null): value is string {
  return !!value && /^[a-f0-9]{24}$/i.test(value);
}

function toValueRecord(value: LookupValue): Record<string, unknown> {
  return {
    ...value,
    _id: value._id ?? value.id,
    id: value.id,
  };
}

function normalizeValueRecord(raw: Record<string, any>): LookupValue {
  const normalizedInspectionModules = normalizeValueModules(raw.inspectionModules ?? raw.requiredModules);
  const normalizedEntityCategories = normalizeLookupEntityCategories(raw.category);
  const rawId = raw._id != null ? String(raw._id) : '';
  const id = String(raw.id ?? raw._id ?? '');
  const normalized: LookupValue = {
    ...raw,
    _id: rawId || id,
    id,
    lookupType: String(raw.lookupType ?? ''),
    code: String(raw.code ?? id),
    name: String(raw.name ?? raw.displayName ?? raw.code ?? id),
    description: raw.description,
    appliesTo: Array.isArray(raw.appliesTo) ? raw.appliesTo : [],
    isActive: raw.isActive ?? true,
    sortOrder: raw.sortOrder ?? 0,
    scope: raw.scope ?? 'system',
    originScope: raw.originScope ?? raw.scope ?? 'system',
    inheritancePolicy: raw.inheritancePolicy ?? 'custom_owned',
    createdAt: raw.createdAt ? new Date(raw.createdAt).toISOString() : undefined,
    updatedAt: raw.updatedAt ? new Date(raw.updatedAt).toISOString() : undefined,
    inspectionModules: normalizedInspectionModules,
    requiredModules: normalizedInspectionModules,
    category: normalizedEntityCategories.length > 0
      ? normalizedEntityCategories
      : (Array.isArray(raw.category) ? raw.category : (typeof raw.category === 'string' ? raw.category : undefined)),
  };
  return normalized;
}

function normalizeValue(doc: any): LookupValue {
  const id = String(doc?._id ?? doc?.id);
  return normalizeValueRecord(doc);
}

function normalizeGroup(doc: any): LookupGroup {
  const id = String(doc?._id ?? doc?.id);
  return {
    ...doc,
    _id: id,
    id,
    isActive: doc?.isActive ?? true,
    isSystemDefault: doc?.isSystemDefault ?? false,
    sortOrder: doc?.sortOrder ?? 0,
    createdAt: doc?.createdAt ? new Date(doc.createdAt).toISOString() : nowIso(),
    updatedAt: doc?.updatedAt ? new Date(doc.updatedAt).toISOString() : nowIso(),
  } as LookupGroup;
}

function normalizeCategory(doc: any): LookupCategory {
  const id = doc?._id ? String(doc._id) : undefined;
  const lookupType = String(doc?.lookupType ?? doc?.code ?? doc?.key ?? id ?? '');
  const displayName = String(doc?.displayName ?? doc?.name ?? lookupType);
  const scopePolicy = doc?.scopePolicy && typeof doc.scopePolicy === 'object'
    ? {
      scopeMode: doc.scopePolicy.scopeMode === 'entity' || doc.scopePolicy.scopeMode === 'module' || doc.scopePolicy.scopeMode === 'entityAndModule'
        ? doc.scopePolicy.scopeMode
        : 'all',
      enforceCategory: Boolean(doc.scopePolicy.enforceCategory),
      enforceModule: Boolean(doc.scopePolicy.enforceModule),
    }
    : DEFAULT_LOOKUP_SCOPE_POLICY;
  return {
    ...doc,
    ...(id ? { _id: id } : {}),
    lookupType,
    displayName,
    description: String(doc?.description ?? ''),
    availableScopes: Array.isArray(doc?.availableScopes) ? doc.availableScopes : ['system', 'organization', 'tenant'],
    manageableScopes: Array.isArray(doc?.manageableScopes) ? doc.manageableScopes : ['system'],
    isSystemManaged: doc?.isSystemManaged ?? false,
    module: normalizeCategoryModules(doc?.module),
    scopePolicy: scopePolicy ?? DEFAULT_LOOKUP_SCOPE_POLICY,
    scope: doc?.scope ?? 'system',
    originScope: doc?.originScope ?? doc?.scope ?? 'system',
    isActive: doc?.isActive ?? true,
    _deleted: doc?._deleted ?? false,
    createdAt: doc?.createdAt ? new Date(doc.createdAt).toISOString() : undefined,
    updatedAt: doc?.updatedAt ? new Date(doc.updatedAt).toISOString() : undefined,
  } as LookupCategory;
}

async function listLocalCategories(): Promise<LookupCategory[]> {
  const db = await getSystemDb();
  const docs = await db.system_lookup_categories.toArray();
  return (docs as any[])
    .map(normalizeCategory)
    .filter((doc) => !doc._deleted)
    .sort((a, b) => (a.displayName || a.lookupType).localeCompare(b.displayName || b.lookupType));
}

async function listLocalValues(lookupType: string): Promise<LookupValue[]> {
  const db = await getSystemDb();
  const docs = await db.system_lookups.toArray();
  return (docs as any[])
    .map(normalizeValue)
    .filter((doc) => doc.lookupType === lookupType && !doc._deleted)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

async function listLocalGroups(lookupType: string): Promise<LookupGroup[]> {
  const db = await getSystemDb();
  const docs = await db.system_lookup_groups.toArray();
  return (docs as any[])
    .map(normalizeGroup)
    .filter((doc) => doc.lookupType === lookupType && !doc._deleted && doc.isActive)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

async function writeCategoryAudit(category: LookupCategory, action: 'create' | 'update' | 'delete', summary: string, before?: Record<string, unknown> | null, after?: Record<string, unknown> | null): Promise<void> {
  const changes = action === 'create' || !before || !after ? [] : computeAuditChanges(before, after);
  await writeAuditLog({
    collection: 'lookups',
    documentId: category.lookupType,
    action,
    summary,
    changes,
  });
}

async function writeScopedLookupAudit(lookupType: string, action: 'create' | 'update' | 'delete', summary: string, before?: Record<string, unknown> | null, after?: Record<string, unknown> | null): Promise<void> {
  const changes = action === 'create' || !before || !after ? [] : computeAuditChanges(before, after);
  await writeAuditLog({
    collection: 'lookups',
    documentId: lookupType,
    action,
    summary,
    changes,
  });
}

async function replaceLocalValue(tempId: string, saved: any): Promise<LookupValue> {
  const db = await getSystemDb();
  const normalized = normalizeValue(saved);
  await db.system_lookups.put(normalized as any);
  if (normalized.id !== tempId) {
    await db.system_lookups.delete(tempId);
  }
  wakeLocalWrite('system_lookups');
  return normalized;
}

async function replaceLocalGroup(tempId: string, saved: any): Promise<LookupGroup> {
  const db = await getSystemDb();
  const normalized = normalizeGroup(saved);
  await db.system_lookup_groups.put(normalized as any);
  if (normalized.id !== tempId) {
    await db.system_lookup_groups.delete(tempId);
  }
  const values = await db.system_lookups.toArray();
  const nextValues = (values as any[]).map((value) => {
    const normalizedValue = normalizeValue(value);
    if (normalizedValue.group?.groupId !== tempId) return null;
    return {
      ...normalizedValue,
      group: {
        groupId: normalized.id,
        groupCode: normalized.code,
        groupName: normalized.name,
        groupIcon: normalized.icon,
        groupColor: normalized.color,
        groupSortOrder: normalized.sortOrder,
      },
      updatedAt: nowIso(),
    };
  }).filter(Boolean);
  await Promise.all(nextValues.map((value) => db.system_lookups.put(value as any)));
  wakeLocalWrite('system_lookup_groups');
  if (nextValues.length > 0) wakeLocalWrite('system_lookups');
  return normalized;
}

async function replaceLocalCategory(tempId: string | undefined, saved: any): Promise<LookupCategory> {
  const db = await getSystemDb();
  const normalized = normalizeCategory(saved);
  await db.system_lookup_categories.put(normalized as any);
  if (tempId && normalized._id && normalized._id !== tempId) {
    await db.system_lookup_categories.delete(tempId);
  }
  wakeLocalWrite('system_lookup_categories');
  return normalized;
}

export async function listLookupCategories(params?: ListParams): Promise<PaginatedResponse<LookupCategory>> {
  const source = await listLocalCategories();

  let filtered = source;
  if (params?.search) {
    const term = params.search.toLowerCase();
    filtered = filtered.filter((category) =>
      category.displayName.toLowerCase().includes(term)
      || category.lookupType.toLowerCase().includes(term)
      || category.description.toLowerCase().includes(term),
    );
  }

  const page = params?.page ?? 1;
  const limit = params?.limit ?? 50;
  const start = (page - 1) * limit;
  return {
    data: filtered.slice(start, start + limit),
    total: filtered.length,
    page,
    limit,
    totalPages: Math.max(1, Math.ceil(filtered.length / limit)),
  };
}

export async function listLookups(params?: {
  lookupType?: string;
  parentCode?: string;
  search?: string;
}): Promise<LookupValue[]> {
  const db = await getSystemDb();
  const docs = await db.system_lookups.toArray();
  let results = (docs as any[])
    .map(normalizeValue)
    .filter((doc) => !doc._deleted);

  if (params?.lookupType) {
    results = results.filter((lookup) => lookup.lookupType === params.lookupType);
  }

  if (params?.parentCode) {
    results = results.filter((lookup) =>
      lookup.config?.parentCode === params.parentCode
      || lookup.config?.stateFips === params.parentCode
      || (typeof lookup.config?.statePostal === 'string'
        && typeof params.parentCode === 'string'
        && lookup.config.statePostal.toLowerCase() === params.parentCode.toLowerCase())
      || lookup.config?.countryCode === params.parentCode
      || (lookup as unknown as Record<string, unknown>).parentCode === params.parentCode
      || (params.lookupType === 'state'
        && (params.parentCode === 'US' || params.parentCode === 'CA')
        && !(lookup as unknown as Record<string, unknown>).parentCode
        && lookup.config?.countryCode == null),
    );
  }

  if (params?.search) {
    const query = params.search.toLowerCase();
    results = results.filter((lookup) =>
      lookup.name.toLowerCase().includes(query)
      || lookup.code.toLowerCase().includes(query)
      || String((lookup as unknown as Record<string, unknown>).label ?? '').toLowerCase().includes(query),
    );
  }

  return results.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
}

export async function getLookup(id: string): Promise<LookupValue | null> {
  const db = await getSystemDb();
  const doc = await db.system_lookups.get(id);
  if (!doc) return null;

  const normalized = normalizeValue(doc);
  return normalized._deleted ? null : normalized;
}

export async function listLookupTypes(): Promise<string[]> {
  const all = await listLookups();
  return Array.from(new Set(all.map((lookup) => lookup.lookupType))).sort();
}

export async function listLookupValues(
  lookupType: string,
  options?: ListLookupValuesOptions,
): Promise<LookupValue[]> {
  const vals = await listLocalValues(lookupType);
  const context: LookupFilterContext = {
    ...options,
    inspectionModules: options?.inspectionModules ?? options?.activeModules,
  };

  return filterLookupValues(
    vals.filter((v) => v.lookupType === lookupType),
    context,
  );
}

export async function listLookupGroups(lookupType: string): Promise<LookupGroup[]> {
  return listLocalGroups(lookupType);
}

export async function saveLookupCategory(category: LookupCategory): Promise<LookupCategory> {
  const db = await getSystemDb();
  const existing = (await db.system_lookup_categories.toArray() as any[])
    .map(normalizeCategory)
    .find((item) => item.lookupType === category.lookupType);
  const tempId = existing?._id ?? category._id ?? `lkc-${category.lookupType}-${Date.now()}`;
  const next: LookupCategory = normalizeCategory({
    ...existing,
    ...category,
    module: normalizeCategoryModules(category.module),
    scopePolicy: category.scopePolicy ?? DEFAULT_LOOKUP_SCOPE_POLICY,
    _id: tempId,
    id: tempId,
    updatedAt: nowIso(),
  });

  await db.system_lookup_categories.put(next as any);
  wakeLocalWrite('system_lookup_categories');

  const before = existing ? { ...existing } as Record<string, unknown> : null;
  const after = { ...next } as Record<string, unknown>;
  await writeCategoryAudit(next, existing ? 'update' : 'create', existing ? `Updated lookup category "${next.displayName}"` : `Created lookup category "${next.displayName}"`, before, after).catch((err) => {
    console.warn('[lookupService] Failed to write category audit log:', err);
  });

  void syncLookupsToAllTenants().catch((err) => {
    console.warn('[lookupService] Background lookup tenant replay failed after category save:', err);
  });

  return next;
}

export async function softDeleteLookupCategory(lookupType: string): Promise<void> {
  const db = await getSystemDb();
  const existing = (await db.system_lookup_categories.toArray() as any[])
    .map(normalizeCategory)
    .find((item) => item.lookupType === lookupType);
  if (!existing) return;
  const next = { ...existing, _deleted: true, isActive: false, updatedAt: nowIso() };
  await db.system_lookup_categories.put(next as any);
  wakeLocalWrite('system_lookup_categories');
  await writeCategoryAudit(next, 'delete', `Deleted lookup category "${next.displayName}"`, { ...existing }, { ...next }).catch((err) => {
    console.warn('[lookupService] Failed to write category delete audit log:', err);
  });
}

export async function saveLookupValue(nextValue: LookupValue): Promise<LookupValue> {
  const db = await getSystemDb();
  const docs = await db.system_lookups.toArray();
  const existing = (docs as any[])
    .find((doc) => String(doc?._id ?? '') === String(nextValue._id ?? ''))
    ?? (docs as any[]).find((doc) => String(doc?.id ?? doc?._id ?? '') === String(nextValue.id));
  const next = normalizeValue({
    ...existing,
    ...nextValue,
    _id: nextValue._id ?? existing?._id ?? nextValue.id,
    id: nextValue.id,
    createdAt: existing?.createdAt ?? nextValue.createdAt ?? nowIso(),
    updatedAt: nowIso(),
  });

  await db.system_lookups.put(next as any);
  wakeLocalWrite('system_lookups');

  const before = existing ? toValueRecord(normalizeValue(existing)) : null;
  const after = toValueRecord(next);
  await writeScopedLookupAudit(next.lookupType, existing ? 'update' : 'create', existing ? `Updated lookup value "${next.name}" (${next.lookupType})` : `Created lookup value "${next.name}" (${next.lookupType})`, before, after).catch((err) => {
    console.warn('[lookupService] Failed to write lookup value audit log:', err);
  });

  return next;
}

export async function setLookupHidden(id: string, hidden: boolean): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.system_lookups.get(id);
  if (!existing) return;
  const next = normalizeValue({ ...existing, isHiddenAtLowerLevels: hidden, updatedAt: nowIso() });
  await db.system_lookups.put(next as any);
  wakeLocalWrite('system_lookups');
  await writeScopedLookupAudit(next.lookupType, 'update', `${hidden ? 'Hid' : 'Unhid'} lookup value "${next.name}" (${next.lookupType})`, toValueRecord(normalizeValue(existing)), toValueRecord(next)).catch((err) => {
    console.warn('[lookupService] Failed to write lookup hidden audit log:', err);
  });
}

export async function softDeleteLookupValue(id: string): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.system_lookups.get(id);
  if (!existing) return;
  const next = normalizeValue({ ...existing, _deleted: true, isActive: false, updatedAt: nowIso() });
  await db.system_lookups.put(next as any);
  wakeLocalWrite('system_lookups');
  await writeScopedLookupAudit(next.lookupType, 'delete', `Deleted lookup value "${next.name}" (${next.lookupType})`, toValueRecord(normalizeValue(existing)), toValueRecord(next)).catch((err) => {
    console.warn('[lookupService] Failed to write lookup value delete audit log:', err);
  });
}

export async function saveLookupGroup(group: LookupGroup): Promise<LookupGroup> {
  const db = await getSystemDb();
  const existing = await db.system_lookup_groups.get(group.id);
  const next = normalizeGroup({
    ...existing,
    ...group,
    _id: group._id ?? group.id,
    id: group.id,
    createdAt: existing?.createdAt ?? group.createdAt ?? nowIso(),
    updatedAt: nowIso(),
  });

  await db.system_lookup_groups.put(next as any);
  wakeLocalWrite('system_lookup_groups');

  const values = await db.system_lookups.toArray();
  const normalizedValues = (values as any[]).map(normalizeValue);
  const prevGroup = existing ? normalizeGroup(existing) : null;
  const groupChanged = !prevGroup
    || prevGroup.name !== next.name
    || prevGroup.icon !== next.icon
    || prevGroup.color !== next.color
    || prevGroup.sortOrder !== next.sortOrder
    || prevGroup.code !== next.code;
  if (groupChanged) {
    const impacted = normalizedValues.filter((value) => value.group?.groupId === next.id);
    await Promise.all(impacted.map((value) => db.system_lookups.put({
      ...value,
      group: {
        groupId: next.id,
        groupCode: next.code,
        groupName: next.name,
        groupIcon: next.icon,
        groupColor: next.color,
        groupSortOrder: next.sortOrder,
      },
      updatedAt: nowIso(),
    } as any)));
    if (impacted.length > 0) wakeLocalWrite('system_lookups');
  }

  await writeScopedLookupAudit(next.lookupType, existing ? 'update' : 'create', existing ? `Updated lookup group "${next.name}" (${next.lookupType})` : `Created lookup group "${next.name}" (${next.lookupType})`, prevGroup ? { ...prevGroup } : null, { ...next }).catch((err) => {
    console.warn('[lookupService] Failed to write lookup group audit log:', err);
  });

  return next;
}

export async function deleteLookupGroup(groupId: string, handleValues: 'ungroup' | 'soft-delete'): Promise<void> {
  const db = await getSystemDb();
  const existing = await db.system_lookup_groups.get(groupId);
  if (!existing) return;
  const group = normalizeGroup(existing);
  const values = await db.system_lookups.toArray();
  const impacted = (values as any[]).map(normalizeValue).filter((value) => value.group?.groupId === groupId);

  const updatedValues = impacted.map((value) => ({
    ...value,
    group: undefined,
    ...(handleValues === 'soft-delete' ? { _deleted: true, isActive: false } : {}),
    updatedAt: nowIso(),
  }));
  await Promise.all(updatedValues.map((value) => db.system_lookups.put(value as any)));
  await db.system_lookup_groups.put({ ...group, _deleted: true, isActive: false, updatedAt: nowIso() } as any);
  wakeLocalWrite('system_lookup_groups');
  if (updatedValues.length > 0) wakeLocalWrite('system_lookups');

  await writeScopedLookupAudit(group.lookupType, 'delete', `Deleted lookup group "${group.name}" (${group.lookupType})`, { ...group }, { ...group, _deleted: true, isActive: false }).catch((err) => {
    console.warn('[lookupService] Failed to write lookup group delete audit log:', err);
  });
  for (const value of impacted) {
    const next = updatedValues.find((item) => item.id === value.id);
    if (!next) continue;
    await writeScopedLookupAudit(value.lookupType, handleValues === 'soft-delete' ? 'delete' : 'update', handleValues === 'soft-delete' ? `Deleted lookup value "${value.name}" (${value.lookupType}) with group removal` : `Ungrouped lookup value "${value.name}" (${value.lookupType})`, toValueRecord(value), toValueRecord(next)).catch((err) => {
      console.warn('[lookupService] Failed to write lookup value group removal audit log:', err);
    });
  }
}

export interface LookupValueOrderPatch {
  id: string;
  sortOrder: number;
  group?: LookupValue['group'];
}

/**
 * @process computeLookupValueReorder
 * Purpose: Compute sortOrder patches so a dragged lookup value can be placed
 *          before/after another value in the same group (or ungrouped list).
 */
export function computeLookupValueReorder(
  allValues: LookupValue[],
  draggedId: string,
  targetId: string,
): LookupValueOrderPatch[] {
  if (draggedId === targetId) return [];
  const live = allValues.filter((value) => !value._deleted);
  const dragged = live.find((value) => value.id === draggedId);
  const target = live.find((value) => value.id === targetId);
  if (!dragged || !target) return [];

  const targetGroupId = target.group?.groupId ?? null;
  const siblings = live
    .filter((value) => (value.group?.groupId ?? null) === targetGroupId)
    .sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

  const fromIndex = siblings.findIndex((value) => value.id === draggedId);
  const withoutDragged = siblings.filter((value) => value.id !== draggedId);
  const targetIndex = withoutDragged.findIndex((value) => value.id === targetId);
  if (targetIndex < 0) return [];
  const movingDown = fromIndex >= 0 && fromIndex < siblings.findIndex((value) => value.id === targetId);
  withoutDragged.splice(movingDown ? targetIndex + 1 : targetIndex, 0, dragged);

  return withoutDragged.flatMap((value, index) => {
    const sortOrder = index + 1;
    const groupChanged = value.id === draggedId && (value.group?.groupId ?? null) !== targetGroupId;
    if (value.sortOrder === sortOrder && !groupChanged) return [];
    return [{ id: value.id, sortOrder, ...(groupChanged ? { group: target.group } : {}) }];
  });
}

/**
 * Persist a drag-handle reorder of system lookup values.
 * Source of Truth: local Dexie `system_lookups`; background push via wakeLocalWrite.
 */
export async function reorderLookupValue(lookupType: string, draggedId: string, targetId: string): Promise<void> {
  const all = await listLocalValues(lookupType);
  const patches = computeLookupValueReorder(all, draggedId, targetId);
  if (patches.length === 0) return;
  const db = await getSystemDb();
  const updatedAt = nowIso();
  await Promise.all(patches.map(async (patch) => {
    const existing = await db.system_lookups.get(patch.id);
    if (!existing) return;
    const normalized = normalizeValue(existing);
    const next = normalizeValue({
      ...normalized,
      sortOrder: patch.sortOrder,
      ...(patch.group !== undefined ? { group: patch.group } : {}),
      updatedAt,
    });
    await db.system_lookups.put(next as any);
  }));
  wakeLocalWrite('system_lookups');
}

export async function bulkAssignValuesToGroup(valueIds: string[], groupId: string | null): Promise<void> {
  const db = await getSystemDb();
  const groups = await db.system_lookup_groups.toArray();
  const group = groupId ? (groups as any[]).map(normalizeGroup).find((item) => item.id === groupId) ?? null : null;
  await Promise.all(valueIds.map(async (valueId) => {
    const existing = await db.system_lookups.get(valueId);
    if (!existing) return;
    const normalized = normalizeValue(existing);
    const next = normalizeValue({
      ...normalized,
      group: group ? {
        groupId: group.id,
        groupCode: group.code,
        groupName: group.name,
        groupIcon: group.icon,
        groupColor: group.color,
        groupSortOrder: group.sortOrder,
      } : undefined,
      updatedAt: nowIso(),
    });
    await db.system_lookups.put(next as any);
    await writeScopedLookupAudit(next.lookupType, 'update', group ? `Moved lookup value "${next.name}" to group "${group.name}"` : `Removed lookup value "${next.name}" from its group`, toValueRecord(normalized), toValueRecord(next)).catch((err) => {
      console.warn('[lookupService] Failed to write lookup value group assignment audit log:', err);
    });
  }));
  wakeLocalWrite('system_lookups');
}

export async function syncLookupsToAllTenants(): Promise<SyncToTenantsResult> {
  return apiFetch('/api/lookups/sync-to-tenants', { method: 'POST' }) as Promise<SyncToTenantsResult>;
}
